/* eslint-disable react/react-in-jsx-scope */
/* eslint-disable react/jsx-filename-extension */
import { DateTime } from 'luxon';

import { isNullOrUndefined } from 'ontraccr-common/lib/Common';
import { Schedule } from 'ontraccr-common';
import { ClockCircleOutlined, CloseCircleOutlined, RollbackOutlined } from '@ant-design/icons';
import { getId as extractId } from '../helpers/helpers';

import Colors from '../constants/Colors';
import {
  QUARTER_HEIGHT,
  RIGHT_OFFSET,
  ITEM_GAP,
  WEEKLY_BASE_OFFSET,
  WEEKLY_HEADER_BASE_HEIGHT,
  WEEKLY_HEADER_PADDING,
  WEEKLY_HEADER_BLOCK_TOTAL_HEIGHT,
  USER_WEEK_HEADER_OFFSET_REDESIGN,
  USER_WEEK_HEADER_OFFSET_LEGACY,
  WEEKLY_HEADER_OFFSET_LEGACY,
} from './schedule.constants';
import { REMINDER_OPTIONS, REMINDER_OPTIONS_MAP } from './reminder/reminder.constants';
import { isObjectEmpty, sortByObjProperty } from '../common/helpers';

import { formatDay, formatRange, getAllDayValues } from '../helpers/time';

export const getId = () => DateTime.local().toMillis();
// Luxon starts week on Mon, so we substract one to make first day Sun
// https://github.com/moment/luxon/issues/373
export const getWeekStart = (day) => (
  day.weekdayShort === 'Sun'
    ? day.startOf('day')
    : day.startOf('week').minus({ day: 1 })
);
export const getWeekEnd = (day) => day.endOf('week').minus({ day: 1 });
export const getBiWeeklyEnd = (day) => {
  const weekstart = getWeekStart(day);
  return weekstart.plus({ day: 14 });
};

/**
 * Returns the visible start/end DateTimes for a schedule view type.
 * Prior to 3.21.0 user day view was just called User view.
 */
export const getViewDateRange = (date, viewType) => {
  const lowercaseViewType = viewType.toLowerCase();
  let dateMod = lowercaseViewType.replace('user', '');
  if (lowercaseViewType === 'user') {
    dateMod = 'day';
  }

  switch (viewType) {
    case 'UserBiWeekly':
      return {
        startDate: getWeekStart(date),
        endDate: getBiWeeklyEnd(date),
      };
    case 'UserWeekdays':
      return {
        startDate: getWeekStart(date).plus({ days: 1 }),
        endDate: getWeekEnd(date).minus({ days: 1 }),
      };
    case 'UserWeek':
      return {
        startDate: date.startOf('week'),
        endDate: date.endOf('week'),
      };
    default:
      return {
        startDate: date.startOf(dateMod),
        endDate: date.endOf(dateMod),
      };
  }
};

export const getTimeOnNewDay = (startOfDay, time) => {
  const timeDT = DateTime.fromMillis(time);
  const newDT = startOfDay.plus({ hours: timeDT.hour, minutes: timeDT.minute });
  return newDT.toMillis();
};

// Luxon weekdays: 1 = Monday ... 7 = Sunday
const FRIDAY = 5;
const MONDAY_OFFSET = 8;

/**
 * Pushes a day landing on Saturday or Sunday forward to the following Monday.
 * @param {DateTime} day
 * @returns {DateTime}
 */
const getNextWeekday = (day) => (
  day.weekday > FRIDAY
    ? day.plus({ days: MONDAY_OFFSET - day.weekday })
    : day
);

/**
 * Resolves company default shift date and/or time in the creator's local timezone.
 * Date and time are independent: either, both, or neither may be configured.
 * @param {object} [settings] company.settings (defaultShift)
 * @param {object} [options]
 * @param {DateTime} [options.baseDay] day to apply default times onto when no default date is set
 * @returns {{
 *   day: DateTime | null,
 *   startTime: number | null,
 *   endTime: number | null,
 * } | null} null if neither date nor valid times are configured
 */
export const getDefaultShiftTimes = (
  settings = {},
  { baseDay = DateTime.local() } = {},
) => {
  const { defaultShift } = settings;
  const {
    date,
    skipWeekends,
    startTime: defaultStartTime,
    endTime: defaultEndTime,
  } = defaultShift ?? {};

  const hasDate = date === 'currentDay' || date === 'nextDay';

  let startHour;
  let startMinute;
  let endHour;
  let endMinute;
  let hasTime = false;
  if (defaultStartTime && defaultEndTime) {
    [startHour, startMinute] = defaultStartTime.split(':').map(Number);
    [endHour, endMinute] = defaultEndTime.split(':').map(Number);

    const totalStartMinutes = (startHour * 60) + startMinute;
    const totalEndMinutes = (endHour * 60) + endMinute;
    hasTime = totalStartMinutes < totalEndMinutes;
  }

  if (!hasDate && !hasTime) return null;

  let day = null;
  if (hasDate) {
    day = DateTime.local().startOf('day');
    if (date === 'nextDay') {
      day = day.plus({ days: 1 });
      if (skipWeekends) {
        day = getNextWeekday(day);
      }
    }
  }

  let startTime = null;
  let endTime = null;
  if (hasTime) {
    const timeDay = (day ?? baseDay).startOf('day');
    startTime = timeDay.set({ hour: startHour, minute: startMinute }).toMillis();
    endTime = timeDay.set({ hour: endHour, minute: endMinute }).toMillis();
  }

  return {
    day,
    startTime,
    endTime,
  };
};

export const getDayKey = (day) => day.toLocaleString(DateTime.DATE_FULL);
export const positionToQuarter = (pos, scale = 1) => Math.floor(pos / (QUARTER_HEIGHT * scale));
export const offsetToQuarter = (offset, scale = 1) => (
  positionToQuarter(offset, scale)
);
export const quarterToText = (quarterHours) => {
  let now = DateTime.local().startOf('day');
  if (!Number.isNaN(quarterHours)) {
    now = now.plus({ minutes: quarterHours * 15 });
  }
  return now.toLocaleString(DateTime.TIME_SIMPLE);
};

export const yToHour = ({
  scrollTop, e, isWeek, scale = 1, headerHeight = 75, containerY, hasRedesign,
  viewType,
}) => {
  const {
    left: parentLeft,
    right: parentRight,
  } = e.currentTarget.getBoundingClientRect();
  const { clientY, clientX } = e;
  const xOffset = clientX - parentLeft;
  let headerOffset;
  if (viewType === 'UserWeek' || viewType === 'UserWeekdays') {
    headerOffset = hasRedesign ? USER_WEEK_HEADER_OFFSET_REDESIGN : USER_WEEK_HEADER_OFFSET_LEGACY;
  } else if (hasRedesign) {
    headerOffset = isWeek ? WEEKLY_BASE_OFFSET + headerHeight : containerY || 200;
  } else {
    headerOffset = isWeek ? WEEKLY_BASE_OFFSET + headerHeight
    - WEEKLY_HEADER_OFFSET_LEGACY : containerY || 160;
  }
  const offset = scrollTop + clientY - headerOffset; // Top offset of div

  // Each 15 minute interval is 25px.
  const quarterHours = offsetToQuarter(offset, scale);
  const safeScale = scale || 1;

  return {
    offset,
    // Snapped slot used when drawing a new shift. Hit-testing should use rawTop
    // so clicks on the top edge are not floored into the slot above the shift.
    top: (quarterHours * 25),
    rawTop: offset / safeScale,
    text: quarterToText(quarterHours),
    xOffset,
    parentWidth: parentRight - parentLeft,
  };
};

export const decorateShiftWithTimes = (shift, day = DateTime.local()) => {
  const { top } = shift;
  let { bottom } = shift;

  if (!bottom) bottom = top + QUARTER_HEIGHT;
  if (Number.isNaN(top) || Number.isNaN(bottom)) return shift;
  const startTime = day
    .startOf('day').plus({ minutes: 15 * (top / QUARTER_HEIGHT) }).toMillis();
  const endTime = day
    .startOf('day').plus({ minutes: 15 * (bottom / QUARTER_HEIGHT) }).toMillis();
  return {
    ...shift,
    startTime,
    endTime,
  };
};

export const timestampToPosition = (ts, reference = DateTime.local().startOf('day')) => {
  const dt = DateTime.fromMillis(ts);
  const diff = dt.diff(reference).as('minutes');
  return (diff / 15) * QUARTER_HEIGHT;
};

export const isInExisting = ({ sortedShifts, shift }) => {
  // xOffset is the x coordinate of the click
  if (shift.xOffset >= shift.parentWidth - 25) return false;
  const xClickPercent = 100 * (shift.xOffset / shift.parentWidth);
  const clickY = shift.rawTop ?? shift.top;

  const existing = sortedShifts.find((existingShift) => (
    clickY >= existingShift.top
    && clickY <= existingShift.bottom
    && xClickPercent >= existingShift.left
    && xClickPercent <= existingShift.left + existingShift.width
  ));

  if (!existing) return false;
  const { top, bottom } = existing;
  const editShift = { ...existing };
  // Use the snapped slot for stretch vs move so the top handle still resizes
  // even when rawTop is several pixels into the shift.
  const stretchPoint = shift.top;
  // Cant drag shifts when they are too small
  if (bottom - top <= 80) return editShift;
  if (Math.abs(stretchPoint - top) <= 30) {
    // User wants to stretch top
    editShift.topStretch = true;
    editShift.cursor = 'ns-resize';
  } else if (Math.abs(bottom - stretchPoint) <= 30) {
    // User wants to stretch end
    editShift.bottomStretch = true;
    editShift.cursor = 'ns-resize';
  } else {
    editShift.topDragOffset = top - clickY;
    editShift.cursor = 'move';
  }
  return editShift;
};

export const SHIFT_BLOCK_STRETCH_HANDLE_PX = 8;

export const getEditShiftFromBlockMouseDown = ({ e, shift, scale = 1 }) => {
  const rect = e.currentTarget.getBoundingClientRect();
  const y = e.clientY - rect.top;
  const editShift = { ...shift };
  const safeScale = scale || 1;
  // Handle size is in screen pixels. A block too short for both handles is a move.
  const canStretch = rect.height > SHIFT_BLOCK_STRETCH_HANDLE_PX * 2;
  if (canStretch && y <= SHIFT_BLOCK_STRETCH_HANDLE_PX) {
    editShift.topStretch = true;
    editShift.cursor = 'ns-resize';
  } else if (canStretch && y >= rect.height - SHIFT_BLOCK_STRETCH_HANDLE_PX) {
    editShift.bottomStretch = true;
    editShift.cursor = 'ns-resize';
  } else {
    editShift.topDragOffset = -(y / safeScale);
    editShift.cursor = 'move';
  }
  return editShift;
};

export const onMouseDown = ({
  day,
  e,
  shifts,
  scrollTop,
  onShiftEdit,
  onNewShiftChange,
  isWeek,
  scale,
  headerHeight,
  containerY,
  hasRedesign,
  viewType,
}) => {
  const sortedShifts = shifts.sort((a, b) => a.top - b.top);
  const shift = yToHour({
    scrollTop, e, isWeek, scale, headerHeight, containerY, hasRedesign, viewType,
  });
  const clickedExisting = isInExisting({ sortedShifts, shift });

  if (clickedExisting) {
    return onShiftEdit(clickedExisting);
  }
  onNewShiftChange({
    ...shift,
    anchorPoint: shift.top,
    id: DateTime.local().toMillis(),
    day,
  });
};

export const onMouseUp = ({
  day,
  e,
  shifts,
  scrollTop,
  editShift,
  onShiftEdit,
  newShift,
  onShiftSelect,
  onNewShiftChange,
  onShiftCreate,
  isWeek,
  scale,
  headerHeight,
  containerY,
  hasRedesign,
  viewType,
}) => {
  document.body.style.cursor = 'auto';
  if (editShift && !isObjectEmpty(editShift)) {
    const selectShift = { ...editShift };
    delete selectShift.topStretch;
    delete selectShift.bottomStretch;
    onShiftEdit(selectShift);
    onShiftSelect(selectShift, true);
    return;
  }

  const doesNotHaveNewShift = !newShift || isObjectEmpty(newShift);
  const clickedExisting = doesNotHaveNewShift && isInExisting({
    sortedShifts: shifts,
    shift: yToHour({
      scrollTop, e, isWeek, scale, headerHeight, containerY, hasRedesign, viewType,
    }),
  });
  if (clickedExisting) {
    onShiftSelect(clickedExisting, true);
    return;
  }

  if (doesNotHaveNewShift) return;
  const shift = {
    ...newShift,
    complete: true,
    day,
  };
  if (!shift.bottom) shift.bottom = shift.top + 100;
  onNewShiftChange(shift);
  onShiftCreate({
    ...decorateShiftWithTimes(shift, day),
    id: getId(),
  });
};

export const onMouseMove = ({
  day,
  e,
  scrollTop,
  editShift,
  newShift,
  onShiftEdit,
  onNewShiftChange,
  isWeek,
  scale,
  headerHeight,
  containerY,
  hasRedesign,
  viewType,
}) => {
  const { top: newTop, offset: newOffset } = yToHour({
    scrollTop, e, isWeek, scale, headerHeight, containerY, hasRedesign, viewType,
  });
  if (editShift && !isObjectEmpty(editShift)) {
    document.body.style.cursor = editShift.cursor;
    const dragTop = newTop + editShift.topDragOffset;
    const afterMove = { ...editShift };
    if (editShift.topStretch) {
      afterMove.top = Math.min(newTop, editShift.bottom - QUARTER_HEIGHT);
    } else if (editShift.bottomStretch) {
      afterMove.bottom = Math.max(newTop, editShift.top + QUARTER_HEIGHT);
    } else if (Number.isFinite(editShift.topDragOffset)) {
      const snappedTop = positionToQuarter(dragTop) * QUARTER_HEIGHT;
      afterMove.top = snappedTop;
      afterMove.bottom = editShift.bottom + (snappedTop - editShift.top);
    }
    onShiftEdit(decorateShiftWithTimes(afterMove, day));
    return;
  }
  if (!newShift || newShift.complete || isObjectEmpty(newShift)) return;
  document.body.style.cursor = 'row-resize';
  const { offset, anchorPoint } = newShift;

  if (newTop >= anchorPoint) {
    const oldQuarter = offsetToQuarter(offset, scale);
    const newQuarter = offsetToQuarter(newOffset, scale);

    onNewShiftChange({
      ...newShift,
      bottom: newQuarter * 25,
      text: `${quarterToText(oldQuarter)} - ${quarterToText(newQuarter)}`,
    });
  } else {
    const newQuarter = offsetToQuarter(newOffset, scale);
    const oldQuarter = offsetToQuarter(offset, scale);
    onNewShiftChange({
      ...newShift,
      top: newTop,
      text: `${quarterToText(newQuarter)} - ${quarterToText(oldQuarter)}`,
    });
  }
};

const includes = (a, b) => a.toLowerCase().includes(b.toLowerCase());

const filterDayShifts = ({
  shifts = [],
  searchText = '',
  userMap = {},
  selectedTitle,
  selectedDivisions,
  selectedUsers,
  selectedProjects,
  selectedCostcodes,
  selectedPhases,
  selectedForms,
  selectedEventType,
  selectedProjectTypes,
  projectMap = {},
  selectedTeams,
  drafts = false,
  published = false,
}) => shifts.filter((shift) => {
  const {
    users = [],
    title,
    description,
    divisionId,
    projectId,
    costcodeId,
    phaseId,
    formTemplateId,
    isEvent,
    teams = [],
    isDraft,
  } = shift;

  if (selectedProjects.size && !selectedProjects.has(projectId)) return false;
  if (selectedCostcodes.size && !selectedCostcodes.has(costcodeId)) return false;
  if (selectedPhases.size && !selectedPhases.has(phaseId)) return false;
  if (selectedDivisions.size && !selectedDivisions.has(divisionId)) return false;
  if (selectedForms.size && !selectedForms.has(formTemplateId)) return false;
  if (selectedUsers.size && !users.some((user) => selectedUsers.has(user))) return false;
  if (!isNullOrUndefined(selectedEventType) && ((selectedEventType === 'event' && !isEvent) || (selectedEventType === 'shift' && isEvent))) return false;
  if (selectedTitle && !includes(title, selectedTitle)) return false;
  if (
    selectedTeams.size
    && !(
      teams.some((team) => selectedTeams.has(team))
      || users.some((userId) => {
        const user = userMap[userId];
        return user?.teams?.some((team) => selectedTeams.has(team.id));
      })
    )
  ) return false;
  if (drafts && !isDraft) return false;
  if (published && isDraft) return false;
  const relevantProject = projectMap[projectId];

  if (
    selectedProjectTypes.size
    && (
      !projectId // if filter exists and shift does not have a project, should be filtered out
      || (
        // or if project type is set and not in the selected project types
        (relevantProject.projectTypeId && !selectedProjectTypes.has(relevantProject?.projectTypeId))
        // or if project type is not set and None is not in selected project types
        || (!relevantProject.projectTypeId && !selectedProjectTypes.has('None'))
      )
    )
  ) return false;

  const relevantUsers = users.some((userId) => userId in userMap
        && userMap[userId].name
        && includes(userMap[userId].name, searchText));
  return (
    (title && includes(title, searchText))
        || (description && includes(description, searchText))
        || relevantUsers
  );
});
export const filterShifts = ({
  shifts = {},
  day = DateTime.local(),
  userMap = {},
  searchText,
  viewType,
  rangeStartDate = DateTime.local(),
  rangeEndDate = DateTime.local(),
  filters = {},
  projectMap = {},
}) => {
  const {
    title,
    eventType,
    users,
    divisions,
    projects,
    costcodes,
    phases,
    forms,
    projectTypes,
    teams,
    drafts,
    published,
  } = filters;
  const selectedUsers = new Set(users);
  const selectedDivisions = new Set(divisions);
  const selectedProjects = new Set(projects);
  const selectedCostcodes = new Set(
    costcodes?.map((cc) => (
      /*
        https://projectharbour.atlassian.net/browse/HARBOUR-6474

        CC looks like `${phaseId}.${costcodeId}`
      */
      cc.split('.').pop()
    ))?.filter((cc) => !!cc) ?? []
  );
  const selectedPhases = new Set(phases);
  const selectedForms = new Set(forms);
  const selectedProjectTypes = new Set(projectTypes);
  const selectedTeams = new Set(teams);

  const res = {};
  const start = viewType === 'Month' ? getWeekStart(day.startOf('month')) : getWeekStart(day);
  let duration = 7;
  if (viewType === 'UserBiWeekly') {
    duration = Math.round(rangeEndDate.diff(rangeStartDate, 'days').as('days'));
  }
  if (viewType === 'Month') {
    duration = Math.ceil(day.endOf('month').endOf('week').diff(start).as('days'));
  }

  new Array(duration).fill(1).forEach((_, idx) => {
    const dayKey = getDayKey(start.plus({ day: idx }));
    const {
      [dayKey]: dayShifts = [],
    } = shifts;
    res[dayKey] = filterDayShifts({
      shifts: dayShifts,
      day,
      searchText,
      userMap,
      selectedTitle: title,
      selectedDivisions,
      selectedUsers,
      selectedProjects,
      selectedCostcodes,
      selectedPhases,
      selectedForms,
      selectedEventType: eventType,
      selectedProjectTypes,
      projectMap,
      selectedTeams,
      drafts,
      published,
    });
  });
  return res;
};

// Used to calculate how wide a shift should be,
// based off the number of interlapping shifts on the same hour of the same day
export const calculateWidths = ({
  dayShifts = [],
  day = DateTime.local(),
  parentWidth,
  leftOffset,
}) => {
  const minBlockWidth = Math.min(parentWidth / 10, 50);
  const dayStart = day.startOf('day');
  // Count all shifts in each quarter to figure out widths
  const quarterCounts = new Array(24 * 4).fill(0);

  const sortedShifts = [...dayShifts];
  sortedShifts.sort((a, b) => (b.endTime - b.startTime) - (a.endTime - a.startTime));
  const shiftMap = {};
  sortedShifts.forEach(({ id, startTime, endTime }) => {
    const start = parseInt(timestampToPosition(startTime, dayStart) / QUARTER_HEIGHT, 10);
    const end = parseInt(timestampToPosition(endTime, dayStart) / QUARTER_HEIGHT, 10);
    shiftMap[id] = { start, end };
    let i = start;
    const upper = end === start ? end + 1 : end;
    while (i < upper) {
      quarterCounts[i] += 1;
      i += 1;
    }
  });
  const newShifts = [];

  // Keep track of which shifts have been allocated so far to put them side by side
  // Need to keep track of how far left we've pushed as well
  const quarterShifts = new Array(24 * 4).fill(0);
  const maxLeft = new Array(24 * 4).fill(leftOffset);

  // Create grid that matches schedule
  const usedBounds = new Array(24 * 4).fill().map(() => new Array(parentWidth).fill(false));

  sortedShifts.forEach((shift) => {
    const { start, end } = shiftMap[shift.id];
    let maxCount = 1;
    let leftCount = 0;
    let currentLeftPush = leftOffset;
    let i = start;
    const upper = end === start ? end + 1 : end;
    while (i < upper) {
      maxCount = Math.max(maxCount, quarterCounts[i]);
      leftCount = Math.max(leftCount, quarterShifts[i]);
      currentLeftPush = Math.max(currentLeftPush, maxLeft[i]);
      quarterShifts[i] += 1;
      i += 1;
    }
    if (Number.isNaN(maxCount)) maxCount = 1;
    if (Number.isNaN(leftCount)) leftCount = 0;
    let width = (parentWidth - leftOffset - RIGHT_OFFSET - ITEM_GAP) / maxCount;
    let left = currentLeftPush + ITEM_GAP;
    const gapWidth = ITEM_GAP * leftCount;
    const expectedLeft = (width * leftCount) + leftOffset + gapWidth;

    if (left > expectedLeft + ITEM_GAP) {
      /*
        Shift is pushed further right than expected
        Scan left to right searching for gaps
      */
      for (let k = leftOffset; k < parentWidth; k += 1) {
        let possibleWidth = width;
        let maxL = k;
        let canFit = true;
        for (let j = start; j < upper; j += 1) {
          let l = k;
          while (l < Math.min(k + width, parentWidth) && !usedBounds[j][l]) {
            l += 1;
          }
          possibleWidth = Math.min(possibleWidth, l - k);
          if (possibleWidth <= minBlockWidth) {
            canFit = false;
            break;
          }
          maxL = Math.max(l, maxL);
        }
        if (canFit) {
          left = k + ITEM_GAP;
          width = possibleWidth - ITEM_GAP;
          break;
        }
        k = maxL;
      }
    }

    let j = start;
    while (j < upper) {
      maxLeft[j] = Math.max(left + width, maxLeft[j]);
      for (let k = parseInt(left, 10); k <= Math.min(parentWidth - 1, left + width); k += 1) {
        usedBounds[j][k] = true;
      }
      j += 1;
    }
    newShifts.push({
      ...shift,
      width: (100 * width) / parentWidth,
      left: (100 * left) / parentWidth,
      zIndex: maxCount - leftCount,
    });
  });

  return newShifts;
};

// Used to calculate the top offset and total height
// for the weekly header, based off interlapping
// All Day or Multi Day tasks on the same day.
export const calculateHeights = ({
  weekStart,
  shifts = [],
}) => {
  // Largest blocks on top
  const sortedShifts = [...shifts].sort((a, b) => (
    (b.endTime - b.startTime) - (a.endTime - a.startTime)
  ));

  const heightOffsets = new Array(7).fill(WEEKLY_HEADER_PADDING);
  let myHeight = WEEKLY_HEADER_BLOCK_TOTAL_HEIGHT + WEEKLY_HEADER_BASE_HEIGHT;
  const blocks = [];
  sortedShifts.forEach((shift) => {
    const {
      startTime,
      endTime,
      timezone = DateTime.local().zoneName,
    } = shift;

    const startInShiftTimezone = DateTime.fromMillis(startTime, { zone: timezone });
    const endInShiftTimezone = DateTime.fromMillis(endTime, { zone: timezone });
    const {
      isAllDay,
    } = getAllDayValues(
      startInShiftTimezone,
      endInShiftTimezone,
    );

    const dayStart = isAllDay
      ? startInShiftTimezone
      : DateTime.fromMillis(startTime).startOf('day');
    const endDt = isAllDay
      ? endInShiftTimezone
      : DateTime.fromMillis(endTime);
    const dayEnd = endDt.equals(endDt.startOf('day'))
      ? endDt.minus({ day: 1 }).endOf('day') : endDt.endOf('day');

    const realWeekStart = isAllDay
      ? DateTime.local().setZone(timezone).set({
        year: weekStart.year,
        month: weekStart.month,
        day: weekStart.day,
        hour: 0,
        minute: 0,
        second: 0,
        millisecond: 0,
      })
      : weekStart;

    const ourStart = DateTime.max(dayStart, realWeekStart);
    const ourEnd = DateTime.min(dayEnd, realWeekStart.plus({ days: 6 }).endOf('day'));
    const dayDuration = Math.round(ourEnd.diff(ourStart).as('days'));
    const firstDay = Math.round(ourStart.diff(realWeekStart).as('days'));

    let myTop = WEEKLY_HEADER_PADDING;
    let i = firstDay;
    while (i < firstDay + dayDuration) {
      const dayHeight = heightOffsets[i];
      myTop = Math.max(dayHeight, myTop);
      i += 1;
    }
    blocks.push({
      shift,
      dayDuration,
      firstDay,
      top: myTop,
    });
    const newHeight = myTop + WEEKLY_HEADER_BLOCK_TOTAL_HEIGHT;
    i = firstDay;
    while (i < firstDay + dayDuration) {
      heightOffsets[i] = newHeight;
      i += 1;
    }
    myHeight = Math.max(myHeight, newHeight + WEEKLY_HEADER_BASE_HEIGHT);
  });

  return {
    blocks,
    height: myHeight,
  };
};

/**
 * Removes duplicate reminders as well as default none reminders
 * @param {array<object>} remindersArr - array of reminders
 * @returns {array<number>} array of unduplicated reminder values
 */
export const refineReminders = (remindersArr = []) => {
  const remindersSet = new Set();
  return remindersArr
    .filter((reminder) => {
      const alreadyAdded = remindersSet.has(reminder.value);
      const isDefault = reminder.value === REMINDER_OPTIONS.DEFAULT_REMINDER.value;
      remindersSet.add(reminder.value);
      return !alreadyAdded && !isDefault;
    })
    .map((reminder) => reminder.value);
};

/**
 * Converts reminder values (minutesBefore) into objects containing value and label
 * @param {array<number>} reminderValues - array of reminder minutesBefore
 * @returns {array<object>}
 */
export const reformatRemindersFromValues = (reminderValues) => reminderValues
  .map((rValue) => REMINDER_OPTIONS_MAP[rValue?.value || rValue]);

/**
 * Converts external reminders ({ value: minutesBefore, recipients })
 * into objects containing value and label
 * @param {array<object>} externalReminders - array of external reminders
 * @returns {array<object>}
 */
export const reformatExternalReminders = (externalReminders) => {
  const valueRecipients = {};
  externalReminders.forEach(({ value, recipient, recipients = [] }) => {
    if (!valueRecipients[value]?.recipients?.length) {
      valueRecipients[value] = {
        value: REMINDER_OPTIONS_MAP[value],
        recipients: [],
      };
    }
    if (recipient) {
      valueRecipients[value].recipients.push(recipient);
    } else {
      valueRecipients[value].recipients.push(...(recipients || []));
    }
  });
  return Object.values(valueRecipients)
    .sort(({ value: valueA }, { value: valueB }) => valueA < valueB);
};

/**
 * Compares whether two sets contain equivalent values
 * @param {set} remindersA
 * @param {set} remindersB
 * @returns
 */
const setsAreEquivalent = (remindersA, remindersB) => {
  if (remindersA.size !== remindersB.size) return false;
  return Array.from(remindersA).every((reminderVal) => remindersB.has(reminderVal));
};

/**
 * Reminders should be updated if:
 *  - the reminders have changed
 *  - repeat has changed
 *  - start time has changed (accounts for day changes)
 * @param {set} prvReminders - reminders before edit
 * @param {set} newReminders - reminders after edit
 * @param {string} prvRepeat - repeat before edit
 * @param {string} newRepeat - repeat after edit
 * @param {string} prvRepeatEndDate - repeat end date before edit
 * @param {string} newRepeatEndDate - repeat end date after edit
 * @param {number} prvStartTime - startTime before edit (in millis)
 * @param {number} newStartTime - startTime after edit (in millis)
 * @returns {boolean} whether reminders should be updated
 */
const shouldUpdateReminders = ({
  prvReminders,
  newReminders,
  prvRepeat,
  newRepeat,
  prvRepeatEndDate,
  newRepeatEndDate,
  prvStartTime,
  newStartTime,
}) => !setsAreEquivalent(prvReminders, newReminders)
  || prvRepeat !== newRepeat
  || prvRepeatEndDate !== newRepeatEndDate
  || prvStartTime !== newStartTime;

export const prepareShiftUpdatePayload = (existingShift = {}, editShift = {}, isGroup = false) => {
  const shouldIncludeRepeat = isGroup
    || editShift.repeat !== existingShift.repeat
    || editShift.repeatEndDate !== existingShift.repeatEndDate;
  const repeat = shouldIncludeRepeat
    ? editShift.repeat
    : null;
  const repeatEndDate = shouldIncludeRepeat
    ? editShift.repeatEndDate
    : null;
  const payload = {
    title: editShift.title,
    description: editShift.description,
    startTime: editShift.startTime,
    endTime: editShift.endTime,
    projectId: editShift.projectId,
    phaseId: editShift.phaseId,
    costcodeId: editShift.costcodeId,
    divisionId: editShift.divisionId,
    repeat,
    repeatEndDate,
    isEvent: !!editShift.isEvent,
    isGroup,
    color: editShift.color,
    reminders: refineReminders(editShift.reminders),
    externalReminders: editShift.externalReminders,
    emailNotification: editShift.emailNotification,
    pushNotification: editShift.pushNotification,
    shouldLockClockIn: !!editShift.shouldLockClockIn,
    teams: editShift.teams,
    expectedUsersToAdd: editShift.expectedUsersToAdd,
    expectedUsersToDelete: editShift.expectedUsersToDelete,
    expectedUsersToUpdate: editShift.expectedUsersToUpdate,
    formTemplateId: editShift.formTemplateId,
    isDraft: !!editShift.isDraft,
    appliedUsers: editShift.appliedUsers,
    categoryId: editShift.categoryId || editShift.shiftCategoryId,
    timezone: editShift.timezone,
    materials: editShift.materials,
    equipment: editShift.equipment,
  };

  if (payload.isEvent) {
    payload.formData = editShift.formData;
  }

  const {
    files: existingFiles = [],
    users: existingUsers = [],
    reminders: existingReminders = [],
    externalReminders: existingExternalReminders = [],
    startTime: existingStartTime,
    repeat: existingRepeat,
    repeatEndDate: existingRepeatEndDate,
  } = existingShift;
  const existingUserSet = new Set(existingUsers);
  const existingFileSet = new Set(existingFiles.map(extractId));
  const existingRemindersSet = new Set(existingReminders);

  const {
    files: newFiles = [],
    users: newUsers = [],
    reminders: newReminders = [],
    externalReminders: newExternalReminders = [],
    startTime: newStartTime,
    repeat: newRepeat,
    repeatendDate: newRepeatEndDate,
  } = editShift;
  const newUserSet = new Set(newUsers);
  const newFileSet = new Set(newFiles.filter(extractId).map(extractId));
  const newRemindersSet = new Set(newReminders);

  payload.shouldUpdateReminders = shouldUpdateReminders({
    prvReminders: existingRemindersSet,
    newReminders: newRemindersSet,
    prvStartTime: existingStartTime,
    newStartTime,
    prvRepeat: existingRepeat,
    newRepeat,
    prvRepeatEndDate: existingRepeatEndDate,
    newRepeatEndDate,
  });

  const existingExternalRemindersSet = new Set();
  existingExternalReminders.forEach(({ value, recipient }) => {
    existingExternalRemindersSet.add(`${value}-${recipient}`);
  });
  const newExternalRemindersSet = new Set();
  newExternalReminders.forEach(({ value, recipients }) => {
    recipients.forEach((recipient) => newExternalRemindersSet.add(`${value}-${recipient}`));
  });

  payload.shouldUpdateExternalReminders = shouldUpdateReminders({
    prvReminders: existingExternalRemindersSet,
    newReminders: newExternalRemindersSet,
    prvStartTime: existingStartTime,
    newStartTime,
    prvRepeat: existingRepeat,
    newRepeat,
    prvRepeatEndDate: existingRepeatEndDate,
    newRepeatEndDate,
  });
  payload.usersToDelete = existingUsers.filter((user) => !newUserSet.has(user));
  payload.filesToDelete = existingFiles
    .filter((file) => file.id && !newFileSet.has(file.id))
    .map(extractId);

  // If the old shift changed from draft to published,
  // then we want to notify all the users of the shift.
  if (existingShift.isDraft && !payload.isDraft) {
    payload.usersToNotify = newUsers;
  }

  payload.usersToAdd = newUsers.filter((user) => !existingUserSet.has(user));
  payload.filesToAdd = newFiles.filter((file) => !file.id || !existingFileSet.has(file.id));

  return payload;
};

const hasDayOverlap = (startDT, endDT, day) => {
  const shiftedDay = day.setZone(startDT.zoneName).set({
    year: day.year,
    month: day.month,
    day: day.day,
    hour: day.hour,
    minute: day.minute,
    second: day.second,
    millisecond: day.millisecond,
  });
  return startDT <= shiftedDay && endDT >= shiftedDay;
};

export const splitMultiDayShifts = (shifts = [], day, daysToIterate) => {
  let multiDay = [];
  let interDay = [];

  if (daysToIterate) {
    multiDay = new Array(daysToIterate).fill().map(() => []);
    interDay = new Array(daysToIterate).fill().map(() => []);
  }

  for (let i = 0; i < (daysToIterate || 1); i += 1) {
    const seenShifts = new Set();
    shifts.forEach((shift) => {
      const {
        startTime, endTime, id, timezone = DateTime.local().zoneName,
      } = shift;
      // Prevent duplicates from being added to the same day
      // Since we are iterating over the same shifts for each day
      if (seenShifts.has(id) && daysToIterate > 1) return;
      seenShifts.add(id);

      if (startTime && endTime && day) {
        const relevantDay = day.plus({ days: i });

        const startInShiftTimezone = DateTime.fromMillis(startTime, { zone: timezone });
        const endInShiftTimezone = DateTime.fromMillis(endTime, { zone: timezone });
        const {
          isAllDay,
        } = getAllDayValues(
          startInShiftTimezone,
          endInShiftTimezone,
        );

        const startDT = isAllDay
          ? startInShiftTimezone
          : DateTime.fromMillis(startTime);
        const endDT = isAllDay
          ? endInShiftTimezone
          : DateTime.fromMillis(endTime);
        const startOfDay = relevantDay.startOf('day');
        const endOfDay = relevantDay.endOf('day');

        if (
          startDT >= startOfDay
          && endDT <= endOfDay
          && !(
            startDT.toMillis() === startOfDay.toMillis()
            && endDT.toMillis() === endOfDay.toMillis()
          )
        ) {
          if (daysToIterate) {
            interDay[i].push(shift);
          } else {
            interDay.push(shift);
          }

          return;
        }

        if (
          hasDayOverlap(startDT, endDT, startOfDay)
          || hasDayOverlap(startDT, endDT, endOfDay)
        ) {
          if (daysToIterate) {
            multiDay[i].push(shift);
          } else {
            multiDay.push(shift);
          }
        }
      }
    });
  }

  return { multiDay, interDay };
};

export const parseTime = ({ day, ourMoment }) => {
  const ts = ourMoment.valueOf();
  return DateTime
    .fromMillis(ts)
    .set({
      day: day.day,
      month: day.month,
      year: day.year,
      second: 0,
      millisecond: 0,
    })
    .toMillis();
};

export const getTextColor = (color = '') => {
  if (color.length < 9) return 'ghostwhite';
  const r = parseInt(color.substring(1, 3), 16);
  const g = parseInt(color.substring(3, 5), 16);
  const b = parseInt(color.substring(5, 7), 16);
  const a = parseInt(color.substring(7, 9), 16);
  if (a < 160) return 'black';
  const total = r * 0.299 + g * 0.587 + b * 0.114;
  return total > 200 ? 'black' : 'ghostwhite';
};

/**
 * Gets shift/event entry background color
 * @param {*} users
 * @param {*} userMap
 * @param {string} color
 * @param {boolean} isMyShift
 * @param {boolean} isEvent
 * @returns color string in hex form (e.g. #FCBA03)
 */
export const getBackgroundColor = (users, userMap, color, isMyShift, isEvent, isDraft) => {
  if (color) return color;
  if (isEvent) return Colors.ONTRACCR_DARK_YELLOW;
  const hasSingleUser = users && users.length === 1;
  const backgroundColor = (isMyShift && !isDraft) ? Colors.ONTRACCR_RED : Colors.ONTRACCR_GRAY;
  if (!hasSingleUser) return backgroundColor;
  const [onlyUserId] = users;
  const { [onlyUserId]: { scheduleColor } = {} } = userMap;
  return (hasSingleUser && scheduleColor && !isDraft) ? scheduleColor : backgroundColor;
};

export const dateRangeValidator = (_, value) => {
  if (!value || value.length === 0 || Number.isNaN(value[0]) || Number.isNaN(value[1])) {
    // Handled by required: true
    return Promise.resolve();
  }
  if (value[0] > value[1]) {
    return Promise.reject(new Error('Start time must be before end time'));
  }
  return Promise.resolve();
};

export const prepareDragShiftUpdatePayload = ({
  shift,
  startOfNewDay,
  previousUserId,
  newUserId,
  emailNotification,
  pushNotification,
  updateUsers,
}) => {
  const startTime = getTimeOnNewDay(startOfNewDay, shift.startTime);
  const endTime = startTime + (shift.endTime - shift.startTime);
  const newUsers = [...shift.users];
  if (updateUsers) {
    const oldUserIndex = newUsers.indexOf(previousUserId);
    if (oldUserIndex !== -1) newUsers.splice(oldUserIndex, 1);
    newUsers.push(newUserId);
  }

  // hardcoded for now, change later
  const newShift = {
    color: shift.color,
    costCodeId: shift.costcodeId,
    dateRange: [startTime, endTime],
    description: shift.description,
    divisionId: shift.divisionId,
    endTime,
    files: shift.files,
    formData: undefined,
    formTemplateId: shift.formTemplateId,
    isEvent: !!shift.isEvent,
    phaseId: shift.phaseId,
    projectId: shift.projectId,
    reminders: shift.reminders,
    repeat: shift.repeat,
    startTime,
    title: shift.title,
    users: newUserId || !updateUsers ? newUsers : [],
  };
  return prepareShiftUpdatePayload(shift, {
    ...shift,
    ...newShift,
    emailNotification,
    pushNotification,
  });
};

export const decorateShift = (shift = {}) => {
  const { startTime, endTime, timezone } = shift;
  const startInShiftTimezone = DateTime.fromMillis(startTime, { zone: timezone });
  const endInShiftTimezone = DateTime.fromMillis(endTime, { zone: timezone });
  const {
    isAllDay,
  } = getAllDayValues(
    startInShiftTimezone,
    endInShiftTimezone,
  );
  const day = DateTime.fromMillis(startTime, {
    zone: isAllDay ? timezone : DateTime.local().zoneName,
  });
  const dayStart = day.startOf('day');
  return {
    ...shift,
    date: startTime,
    day,
    top: timestampToPosition(startTime, dayStart),
    bottom: timestampToPosition(endTime, dayStart),
  };
};

export const addShift = (shifts = {}, shift = {}) => {
  const newShifts = {};
  Object.keys(shifts).forEach((shiftKey) => {
    newShifts[shiftKey] = [...shifts[shiftKey]];
  });
  const newShift = decorateShift(shift);
  const { timezone = DateTime.local().zoneName } = shift;
  let startDay = DateTime.fromMillis(newShift.startTime);
  let endDay = DateTime.fromMillis(newShift.endTime);

  const startInShiftTimezone = DateTime.fromMillis(newShift.startTime, { zone: timezone });
  const endInShiftTimezone = DateTime.fromMillis(newShift.endTime, { zone: timezone });
  const {
    isAllDay,
  } = getAllDayValues(
    startInShiftTimezone,
    endInShiftTimezone,
  );

  if (isAllDay) {
    startDay = startInShiftTimezone;
    endDay = endInShiftTimezone;
  }
  while (startDay <= endDay) {
    const dayKey = getDayKey(startDay);
    if (!(dayKey in shifts)) {
      newShifts[dayKey] = [];
    }
    newShifts[dayKey].push(newShift);
    startDay = startDay.plus({ day: 1 });
  }
  return newShifts;
};

export const deleteShift = (shifts = {}, shift = {}) => {
  const newShifts = { ...shifts };
  const dShift = decorateShift(shift);
  let startDay = DateTime.fromMillis(dShift.startTime);
  const endDay = DateTime.fromMillis(dShift.endTime);
  while (startDay <= endDay) {
    const dayKey = getDayKey(startDay);
    const {
      [dayKey]: dayShifts = [],
    } = shifts;
    newShifts[dayKey] = [...dayShifts.filter((dayShift) => dayShift.id !== dShift.id)];
    startDay = startDay.plus({ day: 1 });
  }
  return newShifts;
};

export const addUsersToMap = (userMap, shift = []) => {
  const newUserMap = { ...userMap };
  const { users = [] } = shift;
  users.forEach((userId) => {
    if (!(userId in newUserMap)) newUserMap[userId] = [];
    newUserMap[userId].push(shift.id);
  });
  return newUserMap;
};

export const createShallowShift = (shift) => {
  const id = 'shadow';
  const defaultColor = '#dddddd';

  const { color } = shift;
  let parsedColor = color;

  if (color?.length > 7) {
    parsedColor = color.substring(0, 7);
  }

  const shadowColor = Colors.opacity(parsedColor ?? defaultColor, 0.5);

  const shadow = {
    id,
    ...shift,
    color: shadowColor,
  };

  return decorateShift(shadow);
};

export const addShallowShiftToShifts = ({
  shift = {},
  shifts = {},
  shiftMap = {},
}) => {
  const { id } = shift;
  let newShift = shift;
  let newShifts = { ...shifts };

  if (id in shiftMap) {
    const oldShift = shiftMap[id];
    newShift = { ...oldShift, ...shift };
    newShifts = deleteShift(newShifts, oldShift);
  }

  return addShift(newShifts, newShift);
};

export const getMaxStartFromRestriction = (weeksRestriction) => (
  DateTime.local()
    .plus({ weeks: weeksRestriction + 1 })
    .startOf('week')
    .minus({ days: 2 })
    .endOf('day')
    .toMillis()
);

export const validateExpectedUsers = ({
  form,
  userToLabel = {},
  userLabelMap = {},
  userMap = {},
  positionMap = {},
}) => new Promise((resolve, reject) => {
  const formValues = form.getFieldsValue();

  const {
    expectedUsers = [],
    users = [],
  } = formValues;

  if (isNullOrUndefined(expectedUsers)) {
    resolve();
    return;
  }

  const errors = Schedule.validateExpectedUsers({
    expectedUsers,
    users,
    userToLabel,
    userLabelMap,
    userMap,
    positionMap,
  });

  form.setFields([
    {
      name: 'expectedUsers',
      errors: errors.length ? [...errors] : [],
    },
  ]);
  form.setFields([
    {
      name: 'users',
      errors: errors.length ? [...errors] : [],
    },
  ]);

  if (errors.length) {
    reject(errors);
    return;
  }

  resolve();
});

export const getStatusIcon = (status, fontSize = '12px', marginRight = '0px') => {
  switch (status) {
    case 'pending':
    case 'accepted':
      return <ClockCircleOutlined style={{ color: 'blue', fontSize, marginRight }} />;
    case 'rejected':
      return <CloseCircleOutlined style={{ color: 'red', fontSize, marginRight }} />;
    case 'withdrawn':
      return <RollbackOutlined style={{ color: 'orange', fontSize, marginRight }} />;
    default:
      return null;
  }
};

export const getDistanceBetweenCoordinatesKm = (lat1, lon1, lat2, lon2) => {
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(lat1))
    * Math.cos(toRad(lat2))
    * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

export const coordsFromAddress = (address) => {
  if (!address) return null;
  const m = address.match(
    /^\s*([-+]?\d+(?:\.\d+)?)\s*,\s*([-+]?\d+(?:\.\d+)?)\s*$/,
  );
  if (!m) return null;
  return [Number(m[1]), Number(m[2])];
};

export function calcProjectCoords(project) {
  if (!project) return null;

  const platRaw = project.lat ?? project.latitude;
  const plngRaw = project.lng ?? project.longitude;
  const lat = Number(platRaw);
  const lng = Number(plngRaw);

  if (
    Number.isFinite(lat)
    && Number.isFinite(lng)
    && !(lat === 0 && lng === 0)
  ) {
    return [lat, lng];
  }

  const parsed = coordsFromAddress(project.address);
  return parsed
    && !(parsed[0] === 0 && parsed[1] === 0)
    ? parsed
    : null;
}

export function calcUserDistances(projectCoords, users) {
  if (!projectCoords) return {};
  const [plat, plng] = projectCoords;
  const out = {};
  users.forEach((u) => {
    const latRaw = u.lat ?? u.latitude;
    const lngRaw = u.lng ?? u.longitude;
    const ulat = Number(latRaw);
    const ulng = Number(lngRaw);
    if (
      Number.isFinite(ulat)
      && Number.isFinite(ulng)
      && !(ulat === 0 && ulng === 0)
    ) {
      out[u.id] = getDistanceBetweenCoordinatesKm(plat, plng, ulat, ulng);
    }
  });
  return out;
}

export const convertFromOneTimezoneToAnother = (timestamp, oldTimezone, newTimezone) => {
  if (oldTimezone === newTimezone) return timestamp;
  const firstDT = DateTime.fromMillis(timestamp, { zone: oldTimezone });
  const zoneDT = DateTime.fromObject({ ...firstDT.toObject(), zone: newTimezone });
  return zoneDT.toMillis();
};

/**
 * Formats a shift's start/end for display, matching common calendar range patterns:
 * - Same-day all-day: "Jul 21, 2026"
 * - Same-day timed:   "Jul 21, 2026 9:00 AM - 5:00 PM"
 * - Multi-day all-day: "Jul 21, 2026 - Jul 23, 2026"
 * - Multi-day timed:   "Jul 21, 2026, 9:00 AM - Jul 23, 2026, 5:00 PM"
 * @param {number} startTime: start time in millis
 * @param {number} endTime: end time in millis
 * @returns {string}
 */
export const formatShiftDateTime = (startTime, endTime) => {
  const start = DateTime.fromMillis(startTime);
  const end = DateTime.fromMillis(endTime);
  const { isAllDay, isMultiDay } = getAllDayValues(start, end);

  if (!isMultiDay) {
    if (isAllDay) return formatDay(startTime);
    return `${formatDay(startTime)} ${formatRange([startTime, endTime], DateTime.TIME_SIMPLE)}`;
  }

  return formatRange(
    [startTime, endTime],
    isAllDay ? DateTime.DATE_MED : DateTime.DATETIME_MED,
  );
};

/**
 * Finds overlapping shifts per user for a given date range.
 * Includes drafts and published shifts; excludes excludeShiftId when editing.
 * @param {number[]} dateRange [startMillis, endMillis]: the date range to check for conflicts
 * @param {string} [excludeShiftId]: shift id to skip (the shift being edited/created)
 * @param {Object} shiftUsers { [userId]: shiftId[] }: the shifts by user
 * @param {Object} shiftMap { [shiftId]: shift }: the shifts by id
 * @param {Object} [projectMap] { [projectId]: project }: the projects by id
 * @param {Object} [categoryMap] { [categoryId]: category }: the categories by id
 * @returns {{ [userId: string]: Array<{
 *   id: string,
 *   title: string,
 *   startTime: number,
 *   endTime: number,
 *   isDraft: boolean,
 *   categoryName: string|undefined,
 *   categoryColor: string|undefined,
 *   projectName: string|undefined,
 * }> }}
 */
export const getUserShiftConflicts = ({
  dateRange,
  excludeShiftId,
  shiftUsers = {},
  shiftMap = {},
  projectMap = {},
  categoryMap = {},
}) => {
  const [rangeStart, rangeEnd] = dateRange ?? [];
  if (rangeStart == null || rangeEnd == null) return {};

  const conflictsByUserId = {};

  Object.entries(shiftUsers).forEach(([userId, shiftIds = []]) => {
    const conflicts = [];

    shiftIds.forEach((shiftId) => {
      if (excludeShiftId && shiftId === excludeShiftId) return;

      const shift = shiftMap[shiftId];
      if (!shift) return;

      const {
        startTime = 0,
        endTime = 0,
        title,
        isDraft,
        projectId,
        shiftCategoryId,
        categoryId,
      } = shift;

      if (startTime >= rangeEnd || endTime <= rangeStart) return;

      const resolvedCategoryId = shiftCategoryId || categoryId;
      const {
        [resolvedCategoryId]: {
          name: categoryName,
          color: categoryColor,
        } = {},
      } = categoryMap;
      const {
        [projectId]: { name: projectName } = {},
      } = projectMap;

      conflicts.push({
        id: shiftId,
        title,
        startTime,
        endTime,
        isDraft: !!isDraft,
        categoryName,
        categoryColor,
        projectName,
      });
    });

    if (conflicts.length === 0) return;

    conflicts.sort(sortByObjProperty('startTime'));
    conflictsByUserId[userId] = conflicts;
  });

  return conflictsByUserId;
};
